// modules/freshcards/src/WhiteLabelEmbeddingAPI.ts
//
// 2026-09-03 (Phase 12) — White-Label Embedding API.
//
// @deprecated as of 2026-09-17 (PROMPT F 12 Phase 5). Use EmbeddingAPIv2
// (`./EmbeddingAPIv2.ts`) instead. v2 introduces mountRuntime() + HostRenderer
// contract, an adapter bundle, and theme/layout/AI/page-binding composition.
// WhiteLabelEmbeddingAPI v1 remains exported for back-compat with hosts that
// have already adopted the v1 schema, but new integrations MUST use v2.
// Will be removed in a future major version (no earlier than FreshCards 2.0).
//
// Generates the assets an external app (e.g. Homeschool App, VibeCoder,
// FreshCloud) needs to embed FreshCards content with a custom theme.
//
// The API produces three artifact kinds:
//   1. embedAccount — a full workspace bundle (theme + layout + URL)
//   2. embedPage   — a single-page bundle
//   3. embedDatabase — a database view bundle
//
// Each bundle is a JSON document the host app embeds via <iframe> or
// a custom <div> with the appropriate data attributes. The bundle
// format is stable (schemaVersion 1) and round-trips through
// ThemeEngine.exportThemeBundle / importThemeBundle.

import { exportThemeBundle, importThemeBundle, bundleHash } from './ThemeEngine';
import type { Theme } from './ThemeAPI';

/** @deprecated Use EmbeddingAPIv2 (`./EmbeddingAPIv2.ts`) instead. */
export const EMBED_SCHEMA_VERSION = 1;

/** @deprecated Use EmbeddingAPIv2 instead. */
export type EmbedKind = 'account' | 'page' | 'database';

/** @deprecated Use EmbeddingAPIv2's `EmbeddingBundleV2` instead. */
export interface EmbedBundle {
  schemaVersion: typeof EMBED_SCHEMA_VERSION;
  kind: EmbedKind;
  /** Stable hash of the bundle. Used as a cache key. */
  hash: string;
  /** URL of the embedded FreshCards surface. */
  url: string;
  /** Optional theme. */
  theme?: Theme;
  /** Optional layout. */
  layout?: any;
  /** Identifier of the embedded entity (pageId, dbId, or 'account'). */
  targetId: string;
  /** When the bundle was generated (ISO). */
  generatedAt: string;
  /** Optional meta. */
  meta?: Record<string, any>;
}

export interface EmbedOptions {
  /** Base URL of the FreshCards deployment. Default: 'https://freshcards.freshvibeapps.com'. */
  baseUrl?: string;
  /** Include layout in the bundle. */
  includeLayout?: boolean;
  /** Custom meta to attach. */
  meta?: Record<string, any>;
}

const DEFAULT_BASE = 'https://freshcards.freshvibeapps.com';

function makeUrl(kind: EmbedKind, targetId: string, baseUrl: string): string {
  switch (kind) {
    case 'account':
      return `${baseUrl}/`;
    case 'page':
      return `${baseUrl}/#/page/${encodeURIComponent(targetId)}`;
    case 'database':
      return `${baseUrl}/#/database/${encodeURIComponent(targetId)}`;
  }
}

function makeBundle(
  kind: EmbedKind,
  targetId: string,
  opts: EmbedOptions = {},
): EmbedBundle {
  const baseUrl = opts.baseUrl ?? DEFAULT_BASE;
  const json = exportThemeBundle({ includeLayout: !!opts.includeLayout });
  const parsed = JSON.parse(json);
  return {
    schemaVersion: EMBED_SCHEMA_VERSION,
    kind,
    hash: bundleHash(),
    url: makeUrl(kind, targetId, baseUrl),
    theme: parsed.theme,
    layout: parsed.layout,
    targetId,
    generatedAt: new Date().toISOString(),
    meta: opts.meta,
  };
}

// ---------------------------------------------------------------------------
// Public API — ALL @deprecated; migrate to EmbeddingAPIv2
// ---------------------------------------------------------------------------

/** @deprecated Use EmbeddingAPIv2 (embedAccount via PageBinding) instead. */
export function embedAccount(opts: EmbedOptions = {}): EmbedBundle {
  return makeBundle('account', 'account', opts);
}

/** @deprecated Use EmbeddingAPIv2 (embedPage via PageBinding) instead. */
export function embedPage(pageId: string, opts: EmbedOptions = {}): EmbedBundle {
  return makeBundle('page', pageId, opts);
}

/** @deprecated Use EmbeddingAPIv2 (embedDatabase via PageBinding) instead. */
export function embedDatabase(dbId: string, opts: EmbedOptions = {}): EmbedBundle {
  return makeBundle('database', dbId, opts);
}

// ---------------------------------------------------------------------------
// Round-trip — import a bundle back into FreshCards
// ---------------------------------------------------------------------------

/** @deprecated Use EmbeddingAPIv2 (mountRuntime) instead. */
export function applyEmbedBundle(bundle: EmbedBundle): { ok: boolean; error?: string } {
  if (bundle.schemaVersion !== EMBED_SCHEMA_VERSION) {
    return { ok: false, error: `Unsupported embed schema version: ${bundle.schemaVersion}` };
  }
  const json = JSON.stringify({
    schemaVersion: 1,
    exportedAt: bundle.generatedAt,
    theme: bundle.theme,
    layout: bundle.layout,
  });
  return importThemeBundle(json);
}

// ---------------------------------------------------------------------------
// HTML snippet helper
// ---------------------------------------------------------------------------

/**
 * Produce an <iframe> HTML string that the host app can inject.
 * The bundle is base64-encoded in the `data-fc-bundle` attribute
 * (and a small `data-fc-hash` for cache keying).
 *
 * @deprecated Use EmbeddingAPIv2 (embedScriptHtml) instead.
 */
export function embedIframeHtml(bundle: EmbedBundle, opts?: { width?: string; height?: string; }): string {
  const w = opts?.width ?? '100%';
  const h = opts?.height ?? '600px';
  const json = JSON.stringify(bundle);
  const b64 = typeof btoa === 'function'
    ? btoa(unescape(encodeURIComponent(json)))
    : Buffer.from(json, 'utf-8').toString('base64');
  return `<iframe data-fc-embed="${bundle.kind}" data-fc-bundle="${b64}" ` +
    `data-fc-hash="${bundle.hash}" src="${bundle.url}" ` +
    `width="${w}" height="${h}" style="border:0" loading="lazy"></iframe>`;
}
